import { createContext, ReactNode, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { GearTypeEnum } from "../types/equipment";
import { NodeModel } from "../types/node";
import { useApi } from "./ApiContext";
import useToast from "./ToastContext";

export type BuoyContextType = {
	buoys: NodeModel[];
	isFetching: boolean;
	registerBuoy: (
		buoyId: string,
		name: string,
		gearType: GearTypeEnum
	) => Promise<{
		errors: { id?: string; name?: string };
		success: boolean;
	}>;
	updateBuoy: (
		buoyId: string,
		name: string
	) => Promise<{
		errors: { id?: string; name?: string };
		success: boolean;
	}>;
	removeBuoyFromAccount: (buoyId: string) => Promise<void>;
};

export const BuoyContext = createContext<BuoyContextType | undefined>(undefined);
BuoyContext.displayName = "BuoyContext";

export const BuoyContextProvider: React.FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
	const { t } = useTranslation();
	const toast = useToast();
	const api = useApi();

	const {
		data: nodes,
		isLoading: isFetching,
		refetch,
	} = useQuery(["buoys"], async () => {
		const response = await api.buoy.getNodes();
		return response.result;
	});

	const buoys: NodeModel[] = useMemo(() => nodes ?? [], [nodes]);

	async function registerBuoy(buoyId: string, name: string, gearType: GearTypeEnum) {
		const response = await api.buoy.addNode(buoyId, name, gearType);
		if (response.isSuccess) {
			refetch();
			toast.toastSuccess(t("buoysPage.buoyAdded"));
			return {
				errors: {},
				success: true,
			};
		}

		toast.toastError(t(response.message));
		return { errors: {}, success: false };
	}

	async function updateBuoy(buoyId: string, name: string) {
		const response = await api.buoy.updateNode(buoyId, name);
		if (response.isSuccess) {
			refetch();
			toast.toastSuccess(t("buoysPage.buoyUpdated"));
			return { errors: {}, success: true };
		}

		toast.toastError(t(response.message));
		return { errors: {}, success: false };
	}

	async function removeBuoyFromAccount(buoyId: string) {
		const response = await api.buoy.removeNode(buoyId);

		if (response.isSuccess) {
			refetch();
			toast.toastSuccess(t("buoysPage.buoyRemovedFromAccount"));
			return;
		}

		toast.toastError(response.message);
	}

	return (
		<BuoyContext.Provider
			value={{
				buoys,
				isFetching,
				registerBuoy,
				updateBuoy,
				removeBuoyFromAccount,
			}}
		>
			{children}
		</BuoyContext.Provider>
	);
};

function useBuoyContext() {
	const context = useContext(BuoyContext);
	if (!context) {
		throw new Error("useBuoyContext() must be used inside a BuoyContext provider");
	}
	return context;
}

export default useBuoyContext;
